import { Component, type ErrorInfo, type ReactNode } from 'react'
import { logger } from '../lib/logger'
import { trackAppError } from '../lib/analytics'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const stack = info.componentStack?.slice(0, 500) ?? ''
    logger.error('React render crash', {
      flow: 'error_boundary',
      endpoint: error.message,
      page: typeof window !== 'undefined' ? window.location.pathname : '',
    })
    trackAppError(error.message, stack)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-black text-white p-8">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="text-gray-400">
              An unexpected error occurred. Please reload the page to continue.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-left text-xs text-red-400 bg-gray-900 p-4 rounded overflow-auto max-h-48">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={this.handleReload}
              className="ds-btn ds-btn-primary px-6 py-2"
            >
              Reload
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
